import { Flex, Text } from '@chakra-ui/react';
import {
  faPlus,
  faTrash,
  faEnvelope,
  faUserPlus
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Modal,
  Button,
  Input,
  FormFeedback,
  ButtonText,
  useToast
} from '@morressier/alembic';
import { IWorkspaceMemberPayload } from '@publishing-engine/domain/actions/v1';
import { inviteUsersSchema } from '@publishing-engine/domain/validation';
import { useRef } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { ensureUser } from 'src/api/queries';
import { FieldLabel } from 'src/components/Field';

type IInvitedUser = { email: '' };

export type IWorkspaceMember = Omit<
  IWorkspaceMemberPayload,
  'workspaceId' | 'organizationId' | 'emailNotify'
>;

type IEmailNotify = boolean;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (userIds: IWorkspaceMember[], emailNotify: IEmailNotify) => void;
  isLoading: boolean;
}

const AddMembers = ({ onAdd, isOpen, onClose, isLoading }: Props) => {
  const toast = useToast();
  const {
    control,
    register,
    formState: { errors },
    clearErrors,
    setError,
    handleSubmit,
    reset
  } = useForm<{ users: IInvitedUser[] }>({
    defaultValues: {
      users: [{ email: '' }]
    },
    resolver: zodResolver(inviteUsersSchema)
  });

  const { fields, append, remove } = useFieldArray({
    name: 'users',
    control: control
  });

  const errorRef = useRef<HTMLDivElement | null>(null);

  const resetForm = () =>
    reset({
      users: [{ email: '' }]
    });

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const removeMember = (index: number) => {
    clearErrors(`users.${index}`);
    remove(index);

    if (fields.length === 1) {
      append({ email: '' });
    }
  };

  const checkDuplicate = (users: IInvitedUser[]) => {
    const emailSet = new Set();
    let firstDuplicate: number | undefined;
    const duplicateIndices: number[] = [];

    users.forEach((users, index) => {
      if (emailSet.has(users.email)) {
        firstDuplicate = firstDuplicate ?? index;
        duplicateIndices.push(index);
      } else {
        emailSet.add(users.email);
      }
    });

    if (duplicateIndices.length > 0) {
      duplicateIndices.forEach(index => {
        setError(`users.${index}.email`, {
          type: 'duplicate',
          message:
            'This email was entered multiple times. Keep only one instance before proceeding.'
        });
      });
    }

    return firstDuplicate;
  };

  const onSubmit = async (
    values: { users: IInvitedUser[] },
    emailNotify: IEmailNotify
  ) => {
    const { users } = values;
    const firstDuplicate = checkDuplicate(users);

    if (!firstDuplicate) {
      let usersResponse: { message: string; userId: string }[];
      try {
        usersResponse = await Promise.all(
          users.map(({ email }) => ensureUser(email))
        );
      } catch (e: any) {
        const description =
          e?.response?.data?.message ||
          'We are unable to invite these Members at this time.';
        return toast({
          variant: 'error',
          title: 'Invites Failed',
          description,
          isClosable: true
        });
      }

      const proposedMemberships: IWorkspaceMember[] = usersResponse.map(
        user => ({
          userId: user.userId,
          roles: ['staff']
        })
      );

      onAdd(proposedMemberships, emailNotify);
      resetForm();
      onClose();
    } else {
      // scroll to first error
      const firstError = errorRef.current?.querySelector(
        `[data-errorId="member-${firstDuplicate}"]`
      );
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const handleAdd = (emailNotify: IEmailNotify) => {
    handleSubmit(values => onSubmit(values, emailNotify))();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      width={{ base: 'calc(100vw - 16px)', md: '960px' } as any}
      closeButtonProps={{
        hidden: true
      }}
    >
      <form onSubmit={handleSubmit(values => onSubmit(values, false))}>
        <Flex
          flexDirection="column"
          gap={6}
          padding={{ base: 4, md: 8 }}
          backgroundColor="surfaceSecondaryFill"
        >
          <Text textStyle="headingTitle">Add Members</Text>

          <Flex
            flexDirection="column"
            gap={2}
            paddingX={{ base: 2, md: 6 }}
            paddingY={2}
            height="60vh"
            overflow={'auto'}
            backgroundColor="surfacePrimaryFill"
            ref={errorRef}
          >
            {fields.map((field, index) => (
              <Flex
                key={field.id}
                flexDirection="column"
                gap={2}
                data-errorId={`member-${index}`}
                id={`member-${index}`}
              >
                <FieldLabel
                  label="Email"
                  subText="Provide the Member’s professional email."
                  isRequired
                />

                <Input
                  placeholder="e.g., fabius@molecularconnections.com"
                  {...register(`users.${index}.email`)}
                  invalid={!!errors.users?.[index]?.email}
                />

                <Flex>
                  {errors.users?.[index]?.email && (
                    <FormFeedback variant="error">
                      {errors.users?.[index]?.email?.message}
                    </FormFeedback>
                  )}
                  <ButtonText
                    marginStart="auto"
                    variant="caution"
                    iconLeft={<FontAwesomeIcon icon={faTrash} />}
                    onClick={() => removeMember(index)}
                  >
                    Delete Member
                  </ButtonText>
                </Flex>
              </Flex>
            ))}

            <Flex justifyContent="flex-end">
              <ButtonText
                alignSelf="baseline"
                iconLeft={<FontAwesomeIcon icon={faPlus} />}
                onClick={() => append({ email: '' })}
              >
                Add Another Member
              </ButtonText>
            </Flex>
          </Flex>
          <Flex
            justifyContent="flex-end"
            gap={2}
            alignItems="center"
            marginTop={4}
            width="100%"
          >
            <Button
              onClick={handleClose}
              variant="secondary"
              isDisabled={isLoading}
            >
              Exit Without Adding
            </Button>
            <Button
              variant="primary"
              type="button"
              iconLeft={<FontAwesomeIcon icon={faUserPlus} />}
              isDisabled={isLoading}
              onClick={() => handleAdd(false)}
            >
              Add Members
            </Button>
            <Button
              variant="primary"
              type="button"
              iconLeft={<FontAwesomeIcon icon={faEnvelope} />}
              isDisabled={isLoading}
              isLoading={isLoading}
              onClick={() => handleAdd(true)}
            >
              Add and Notify Members
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
};

export default AddMembers;
